import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  MinusCircleFilled,
} from "@ant-design/icons";
import { Button, Flex, Popconfirm, Progress, Typography } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { getClientDetailsByUUID } from "../../service/client.service";
import { getNotesDetails } from "../../service/notes.service";
import { setLoadingNotesState, setNotesViewVisible, setSelectedClient } from "../../slices/appStateSlice";
import { setNotesDetails, setRecordingId } from "../../slices/notesSlice";
import type { EHRTransferStatus, TransferJob } from "../../types/index.type";
import { useTransfer } from "../TransferButton/TransferContext";

const { Text } = Typography;

// Coarse mapping of statuses → percent. Not exact, but conveys forward
// motion; matches the wireframe's compact fixed-width progress bar.
const STATUS_PROGRESS: Record<EHRTransferStatus, number> = {
  not_started: 0,
  pending: 10,
  logging_in: 25,
  awaiting_mfa: 30,
  patient_lookup: 45,
  awaiting_appointment: 55,
  awaiting_note_type: 60,
  template_mapping: 75,
  writing_note: 90,
  completed: 100,
  failed: 100,
  cancelled: 100,
};

const SLOW_THRESHOLD_MS = 300_000;
const COMPLETED_DISMISS_MS = 4_000;
const BANNER_HEIGHT_PX = 36;

const isAwaitingAction = (status: EHRTransferStatus) =>
  status === "awaiting_appointment" || status === "awaiting_note_type" || status === "awaiting_mfa";

const isTerminal = (status: EHRTransferStatus) =>
  status === "completed" || status === "failed" || status === "cancelled";

/**
 * The banner host: renders one row per in-flight (or just-finished) transfer.
 * Multiple transfers can run at once, so each gets its own row + cancel button.
 */
const TransferBanner: React.FC = () => {
  const { activeJobs, cancelTransfer } = useTransfer();
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  const visibleJobs = activeJobs.filter((j) => !dismissed[j.id]);
  const visibleCount = visibleJobs.length;

  // Publish total banner height as a CSS var so pages that hard-code
  // viewport-based heights can subtract us. Height scales with row count.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--banner-height", `${visibleCount * BANNER_HEIGHT_PX}px`);
    return () => root.style.setProperty("--banner-height", "0px");
  }, [visibleCount]);

  if (visibleCount === 0) return null;

  return (
    <div style={{ flex: "0 0 auto" }}>
      {visibleJobs.map((job) => (
        <TransferBannerRow
          key={job.id}
          job={job}
          onDismiss={(id) => setDismissed((prev) => ({ ...prev, [id]: true }))}
          onCancel={cancelTransfer}
        />
      ))}
    </div>
  );
};

interface RowProps {
  job: TransferJob;
  onDismiss: (id: string) => void;
  onCancel: (id: string) => Promise<void>;
}

const TransferBannerRow: React.FC<RowProps> = ({ job, onDismiss, onCancel }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { rootPath } = useRootPath();
  const { notesDetails } = useAppSelector((state) => state.notes);
  const [cancelling, setCancelling] = useState(false);

  const status = job.status;
  const terminal = isTerminal(status);

  // Tick once a second so "30s+" copy and elapsed time stay fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (terminal) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [terminal]);

  // Auto-dismiss the flash for terminal rows.
  useEffect(() => {
    if (!terminal) return;
    const t = setTimeout(() => onDismiss(job.id), COMPLETED_DISMISS_MS);
    return () => clearTimeout(t);
  }, [terminal, job.id, onDismiss]);

  const isFailed = status === "failed";
  const isCancelled = status === "cancelled";
  const isCompleted = status === "completed";
  const awaitingAction = isAwaitingAction(status);

  const elapsedMs = job.created_at ? Date.now() - new Date(job.created_at).getTime() : 0;
  const isSlow = elapsedMs >= SLOW_THRESHOLD_MS && !awaitingAction && !terminal;

  // The "Go to note" CTA only shows when the user is somewhere else.
  const isOnNote = job.audio_file_id != null && notesDetails?.id === job.audio_file_id;

  const handleGoToNote = async () => {
    if (!job.audio_file_id || !job.client_uuid) return;
    try {
      const [noteRes, clientRes] = await Promise.all([
        getNotesDetails(job.audio_file_id),
        getClientDetailsByUUID(job.client_uuid),
      ]);
      dispatch(setLoadingNotesState(true));
      dispatch(setSelectedClient(clientRes.data));
      dispatch(setNotesDetails(noteRes.data));
      dispatch(setRecordingId(job.audio_file_id));
      dispatch(setNotesViewVisible(true));
      navigate(`${rootPath}/clients/${job.client_uuid}`);
    } catch (err) {
      console.error("Failed to deep-link to transfer note:", err);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await onCancel(job.id);
    } finally {
      setCancelling(false);
    }
  };

  const secondary = secondaryCopy(job);

  return (
    <div style={containerStyle(status)}>
      <Flex align="center" gap={12} style={{ width: "100%" }}>
        <Icon status={status} />
        <Text ellipsis style={{ color: textColor(status), flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600 }}>
            {primaryCopy(job, awaitingAction, isFailed, isCompleted, isCancelled, isSlow)}
          </span>
          {secondary && <span style={{ opacity: 0.75, marginLeft: 8 }}>· {secondary}</span>}
        </Text>
        <Progress
          percent={STATUS_PROGRESS[status] ?? 0}
          showInfo={false}
          status={isFailed || isCancelled ? "exception" : isCompleted ? "success" : "active"}
          strokeColor={
            isFailed
              ? "#ff4d4f"
              : isCancelled
                ? "#8c8c8c"
                : isCompleted
                  ? "#52c41a"
                  : awaitingAction
                    ? "#faad14"
                    : "#1677ff"
          }
          style={{ width: 180, marginBottom: 0 }}
        />
        {!isOnNote && !terminal && job.audio_file_id != null && (
          <Button size="small" onClick={handleGoToNote} type={awaitingAction ? "primary" : "default"}>
            Go to note
          </Button>
        )}
        {!terminal && (
          <Popconfirm
            title="Cancel this transfer?"
            description="The note won't be written to the EHR."
            okText="Cancel transfer"
            okButtonProps={{ danger: true }}
            cancelText="Keep going"
            onConfirm={handleCancel}
          >
            <Button size="small" danger type="text" loading={cancelling}>
              Cancel
            </Button>
          </Popconfirm>
        )}
      </Flex>
    </div>
  );
};

const Icon: React.FC<{ status: EHRTransferStatus }> = ({ status }) => {
  if (status === "completed") return <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />;
  if (status === "failed") return <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />;
  if (status === "cancelled") return <MinusCircleFilled style={{ color: "#8c8c8c", fontSize: 18 }} />;
  if (isAwaitingAction(status)) return <ExclamationCircleFilled style={{ color: "#faad14", fontSize: 18 }} />;
  return <LoadingOutlined style={{ color: "#1677ff", fontSize: 18 }} spin />;
};

const containerStyle = (status: EHRTransferStatus): React.CSSProperties => {
  const base: React.CSSProperties = {
    height: BANNER_HEIGHT_PX,
    padding: "0 16px",
    borderBottom: "1px solid",
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
  };
  if (status === "completed") return { ...base, background: "#f6ffed", borderColor: "#b7eb8f" };
  if (status === "failed") return { ...base, background: "#fff1f0", borderColor: "#ffa39e" };
  if (status === "cancelled") return { ...base, background: "#fafafa", borderColor: "#d9d9d9" };
  if (isAwaitingAction(status)) return { ...base, background: "#fffbe6", borderColor: "#ffe58f" };
  return { ...base, background: "#e6f4ff", borderColor: "#91caff" };
};

const textColor = (status: EHRTransferStatus): string => {
  if (status === "completed") return "#237804";
  if (status === "failed") return "#a8071a";
  if (status === "cancelled") return "#595959";
  if (isAwaitingAction(status)) return "#874d00";
  return "#003a8c";
};

const primaryCopy = (
  job: TransferJob,
  awaitingAction: boolean,
  isFailed: boolean,
  isCompleted: boolean,
  isCancelled: boolean,
  isSlow: boolean,
): string => {
  const noteLabel = job.patient_name ? `${job.patient_name}'s note` : "your note";
  const ehr = job.ehr_name || "your EHR";

  if (isCompleted) return `${noteLabel} was transferred to ${ehr}`;
  if (isFailed) return `Couldn't transfer ${noteLabel} to ${ehr}`;
  if (isCancelled) return `Transfer cancelled for ${noteLabel}`;
  if (awaitingAction) {
    if (job.status === "awaiting_mfa") return `Action needed — enter the verification code`;
    if (job.status === "awaiting_note_type") return `Action needed — choose a note template`;
    return `Action needed — pick the appointment for ${job.patient_name}`;
  }
  if (isSlow) return `Transferring ${noteLabel} to ${ehr} — this may take a bit longer`;
  return `Transferring ${noteLabel} to ${ehr}`;
};

const secondaryCopy = (job: TransferJob): string => {
  const parts: string[] = [];
  if (job.patient_name) parts.push(job.patient_name);
  if (job.ehr_name) parts.push(job.ehr_name);
  return parts.join(" · ");
};

export default TransferBanner;
